import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import {
  stroot,
  organisationUsers,
  organisation as organisationAPI,
  onboarding as onboardingAPI,
  subscription as subscriptionAPI,
} from '@birdi/js-sdk';
import BirdiLogoSVG from '@birdi/icons/birdi-logo-navy.svg';
import InviteIcon from '@birdi/icons/onboarding-invite.svg';
import EmailIcon from '@birdi/icons/onboarding-at.svg';
import InfoSVG from '@birdi/icons/onboarding-info.svg';
import TrashIcon from '@birdi/icons/trash.svg';
import { useMetaSelector } from '../../../meta/reducer';
import { ProgressBar } from '../progress-bar';
import * as styles from '../styles';
import { goToDashboard } from './organisation-setup';
import Select from 'react-select';
import { forms } from '@birdi/theme';
import isEmail from 'validator/lib/isEmail';

interface InviteYourTeamProps {}

interface TeamMember {
  email: string;
  roleId: string | null;
}

interface RoleOption {
  value: string;
  label: string;
}

const InviteYourTeam: React.FC<InviteYourTeamProps> = () => {
  const theme = useTheme() as BirdiTheme;
  const navigate = useNavigate();
  const meta = useMetaSelector();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    { email: '', roleId: null },
  ]);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [sendInviteEmail, setSendInviteEmail] = useState(true);
  const [errors, setErrors] = useState<{
    [key: number]: string;
    general?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [planLimits, setPlanLimits] = useState<{
    userLimitSeats: number | null;
    guestsLimitSeats: number | null;
  } | null>(null);

  // Load roles from API
  useEffect(() => {
    organisationAPI
      .getRoles(stroot('ohw4ae'))
      .then((response: any) => {
        if (response.body && response.body.items) {
          const roles = response.body.items
            .filter(
              (role: any) =>
                role.name === 'Workspace Admin' ||
                role.name === 'Workspace User' ||
                role.name === 'Guest',
            )
            .map((role: any) => ({
              value: role.id,
              label: role.name,
            }));
          setRoleOptions(roles);
        }
      })
      .catch((err) => {
        console.error('Error loading roles:', err);
      });
  }, []);

  // Load plan limits from API
  useEffect(() => {
    subscriptionAPI
      .getCurrentPlan(stroot('phahc4'))
      .then((res: any) => {
        if (res.body?.basePlan) {
          setPlanLimits({
            userLimitSeats: res.body.basePlan.userLimitSeats ?? null,
            guestsLimitSeats: res.body.basePlan.guestsLimitSeats ?? null,
          });
        }
      })
      .catch((err) => {
        // Silently fail - limits might not be available yet
        console.log('Could not fetch plan limits:', err);
      });
  }, []);

  const handleEmailChange = (index: number, email: string) => {
    const updated = [...teamMembers];
    updated[index].email = email;
    setTeamMembers(updated);

    // Clear error for this field
    if (errors[index]) {
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  const handleRoleChange = (index: number, roleOption: RoleOption | null) => {
    const updated = [...teamMembers];
    updated[index].roleId = roleOption?.value || null;
    setTeamMembers(updated);

    // Clear error for this field
    if (errors[index]) {
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  const handleAddAnother = () => {
    // Only add if the last member has both email and role filled
    const lastMember = teamMembers[teamMembers.length - 1];
    if (lastMember.email && lastMember.roleId) {
      setTeamMembers([...teamMembers, { email: '', roleId: null }]);
    }
  };

  const handleDelete = (index: number) => {
    // Don't allow deleting if there's only one row
    if (teamMembers.length <= 1) {
      return;
    }
    const updated = teamMembers.filter((_, i) => i !== index);
    setTeamMembers(updated);

    // Clear errors for deleted index and reindex remaining errors
    const newErrors: { [key: number]: string; general?: string } = {};
    Object.keys(errors).forEach((key) => {
      if (key === 'general') {
        newErrors.general = errors.general;
      } else {
        const errorIndex = parseInt(key, 10);
        if (errorIndex < index) {
          newErrors[errorIndex] = errors[errorIndex];
        } else if (errorIndex > index) {
          newErrors[errorIndex - 1] = errors[errorIndex];
        }
      }
    });
    setErrors(newErrors);
  };

  const handleSkip = () => {
    goToDashboard(meta, navigate);
  };

  const handleInvite = async () => {
    // Validate all fields
    const newErrors: { [key: number]: string } = {};
    const currentUserEmail = meta.email?.toLowerCase();

    // First pass: basic validation and duplicate check
    const emailSet = new Set<string>();
    teamMembers.forEach((member, index) => {
      if (!member.email) {
        newErrors[index] = 'Email is required';
      } else if (!isEmail(member.email)) {
        newErrors[index] = 'Please enter a valid email address';
      } else {
        const emailLower = member.email.toLowerCase();
        // Check for duplicate emails
        if (emailSet.has(emailLower)) {
          newErrors[index] = 'This email has already been added';
        } else {
          emailSet.add(emailLower);
        }
        // Check if email matches current user
        if (currentUserEmail && emailLower === currentUserEmail) {
          newErrors[index] = 'You cannot invite yourself';
        }
      }
      if (!member.roleId) {
        newErrors[index] = newErrors[index] || 'Role is required';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Second pass: validate role limits
    // Get role names to identify Guest role
    const guestRoleId = roleOptions.find(
      (role) => role.label === 'Guest',
    )?.value;
    const guestMembers = teamMembers.filter(
      (member) => member.roleId === guestRoleId,
    );
    const paidMembers = teamMembers.filter(
      (member) => member.roleId !== guestRoleId,
    );

    // Check guest limit using fetched plan limits
    // Note: null, undefined, or 0 means unlimited (no limit)
    const guestLimit = planLimits?.guestsLimitSeats;
    let limitError: string | null = null;

    if (guestLimit && guestLimit > 0) {
      if (guestMembers.length > guestLimit) {
        limitError = `Guest limit exceeded. Your plan allows ${guestLimit} guest${guestLimit !== 1 ? 's' : ''}.`;
      }
    }

    // Check paid user limit (minus 1 for current user)
    // Note: null, undefined, or 0 means unlimited (no limit)
    const userLimit = planLimits?.userLimitSeats;
    if (userLimit && userLimit > 0) {
      const availablePaidSeats = userLimit - 1; // Current user is already one paid user
      if (paidMembers.length > availablePaidSeats) {
        limitError = `User limit exceeded. Your plan allows ${userLimit} paid user${userLimit !== 1 ? 's' : ''} (${availablePaidSeats} available after your account).`;
      }
    }

    // If there are field errors or limit errors, show them
    if (Object.keys(newErrors).length > 0 || limitError) {
      setErrors({ ...newErrors, ...(limitError && { general: limitError }) });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare invites array
      const invites = teamMembers.map((member) => ({
        email: member.email,
        roleId: member.roleId!,
      }));

      // Call API to invite users
      await organisationUsers.inviteUsers(stroot('ahwi9i'), {
        invites,
        sendInviteEmail: sendInviteEmail,
      });

      // Success - redirect to dashboard
      goToDashboard(meta, navigate);
    } catch (error: any) {
      console.error('Error inviting team members:', error);
      // Show general error message
      const generalError =
        error?.body?.message ||
        error?.message ||
        'Failed to invite team members. Please try again.';
      setErrors({ ...errors, general: generalError });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Logo - Fixed at top */}
      <div css={styles.logoContainer}>
        <BirdiLogoSVG />
      </div>

      {/* Scrollable Content */}
      <div css={styles.scrollableContent}>
        {/* Header Section */}
        <div css={styles.headerSection}>
          <div css={styles.iconContainer}>
            <div css={styles.iconWrapper(theme)}>
              <InviteIcon />
            </div>
            <h1 css={styles.title}>Invite your team</h1>
          </div>

          <ProgressBar currentStep={6} totalSteps={6} />

          <p css={styles.subtitle}>
            Add team members to start collaborating on your projects.
          </p>
        </div>

        {/* Form */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
          `}
        >
          {/* Email Addresses */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 1rem;
            `}
          >
            {teamMembers.map((member, index) => (
              <div
                key={index}
                css={css`
                  display: flex;
                  gap: 0.75rem;
                  align-items: flex-start;
                `}
              >
                <div
                  css={css`
                    flex: 1.75;
                    min-width: 0;
                  `}
                >
                  {index === 0 && (
                    <label css={styles.label} htmlFor={`email-${index}`}>
                      Email address
                      <span className="required">*</span>
                    </label>
                  )}
                  <div css={styles.inputWrapper}>
                    <input
                      id={`email-${index}`}
                      type="email"
                      css={(theme: BirdiTheme) => css`
                        ${styles.input(theme)} padding-left: 0.5rem;
                      `}
                      placeholder="colleague@company.com"
                      value={member.email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                    />
                  </div>
                  {errors[index] && (
                    <div css={styles.errorMessage}>{errors[index]}</div>
                  )}
                </div>
                <div
                  css={css`
                    flex: 1.25;
                    min-width: 0;
                    max-width: 250px;
                  `}
                >
                  {index === 0 && (
                    <label css={styles.label} htmlFor={`role-${index}`}>
                      Role
                      <span className="required">*</span>
                    </label>
                  )}
                  <Select
                    inputId={`role-${index}`}
                    instanceId={`role-${index}`}
                    options={roleOptions}
                    placeholder="Select role"
                    value={
                      member.roleId
                        ? roleOptions.find((r) => r.value === member.roleId)
                        : null
                    }
                    onChange={(option: any) => {
                      handleRoleChange(index, option as RoleOption | null);
                    }}
                    styles={
                      {
                        control: (base: any, state: any) => ({
                          ...base,
                          paddingLeft: '0.5rem',
                          minHeight: '2.5rem',
                          borderRadius: '0.5rem',
                          width: '100%',
                        }),
                        valueContainer: (base: any) => ({
                          ...base,
                          padding: '0.5rem 0',
                          textAlign: 'left',
                        }),
                        singleValue: (base: any) => ({
                          ...base,
                          textAlign: 'left',
                        }),
                      } as any
                    }
                  />
                </div>
                {/* Delete Button */}
                <button
                  type="button"
                  onClick={() => handleDelete(index)}
                  disabled={teamMembers.length <= 1}
                  css={(theme: BirdiTheme) => css`
                    background: none;
                    border: none;
                    cursor: ${teamMembers.length <= 1
                      ? 'not-allowed'
                      : 'pointer'};
                    padding: 0;
                    margin-top: ${index === 0 ? '2rem' : '0'};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: ${theme.mono60};
                    transition: color 0.2s;
                    flex-shrink: 0;

                    &:hover:not(:disabled) {
                      color: ${theme.mono70};
                    }

                    &:disabled {
                      opacity: 0.3;
                    }

                    svg {
                      width: 1.25rem;
                      height: 1.25rem;
                    }
                  `}
                  aria-label="Delete row"
                >
                  <TrashIcon />
                </button>
              </div>
            ))}

            {/* Add Another Button */}
            <button
              type="button"
              onClick={handleAddAnother}
              disabled={
                !teamMembers[teamMembers.length - 1].email ||
                !teamMembers[teamMembers.length - 1].roleId
              }
              css={(theme: BirdiTheme) => css`
                align-self: flex-start;
                background: none;
                border: none;
                color: ${theme.deepOcean};
                font-size: 0.875rem;
                font-weight: 500;
                cursor: pointer;
                padding: 0;
                margin-top: -0.5rem;
                transition: opacity 0.2s;

                &:hover:not(:disabled) {
                  text-decoration: underline;
                }

                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
            >
              + Add another
            </button>
          </div>

          {/* Permission Levels Info */}
          <div
            css={(theme: BirdiTheme) => css`
              background-color: ${theme.lightOcean};
              border-radius: 0.5rem;
              padding: 1rem;
              display: flex;
              gap: 0.75rem;
              align-items: flex-start;
            `}
          >
            <div
              css={(theme: BirdiTheme) => css`
                width: 1.25rem;
                height: 1.25rem;
                min-width: 1.25rem;
                color: ${theme.deepOcean};
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            >
              <InfoSVG />
            </div>
            <div
              css={css`
                flex: 1;
              `}
            >
              <h3
                css={(theme: BirdiTheme) => css`
                  font-size: 0.875rem;
                  font-weight: 600;
                  color: ${theme.deepOcean};
                  margin: 0 0 0.5rem 0;
                `}
              >
                Permission levels
              </h3>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 0.5rem;
                `}
              >
                <p
                  css={(theme: BirdiTheme) => css`
                    font-size: 0.8125rem;
                    color: ${theme.deepOcean};
                    margin: 0;
                    line-height: 1.5;
                  `}
                >
                  <strong>Workspace Admin:</strong> Full map access, can update
                  branding, and invite team members
                </p>
                <p
                  css={(theme: BirdiTheme) => css`
                    font-size: 0.8125rem;
                    color: ${theme.deepOcean};
                    margin: 0;
                    line-height: 1.5;
                  `}
                >
                  <strong>Workspace User:</strong> Can create and edit maps
                </p>
                <p
                  css={(theme: BirdiTheme) => css`
                    font-size: 0.8125rem;
                    color: ${theme.deepOcean};
                    margin: 0;
                    line-height: 1.5;
                  `}
                >
                  <strong>Guest:</strong> Can view, download and comment on maps
                </p>
              </div>
            </div>
          </div>

          {/* Send Invite Email Toggle */}
          <div
            css={(theme: BirdiTheme) => css`
              border: 1px solid ${theme.mono30};
              border-radius: 0.5rem;
              padding: 1rem;
              display: flex;
              align-items: center;
              gap: 0.75rem;
              margin-top: 0.5rem;
            `}
          >
            <label
              htmlFor="sendInviteEmail"
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
                cursor: pointer;
                flex: 1;
              `}
            >
              <span
                css={(theme: BirdiTheme) => css`
                  font-size: 0.875rem;
                  color: ${theme.mono70};
                  font-weight: 500;
                `}
              >
                Send invite email
              </span>
              <span
                css={(theme: BirdiTheme) => css`
                  font-size: 0.8125rem;
                  color: ${theme.mono60};
                `}
              >
                Notify team members about the invitation
              </span>
            </label>
            <button
              type="button"
              role="switch"
              aria-checked={sendInviteEmail}
              aria-labelledby="sendInviteEmail"
              onClick={() => setSendInviteEmail(!sendInviteEmail)}
              css={(theme: BirdiTheme) => css`
                position: relative;
                width: 3rem;
                height: 1.5rem;
                border-radius: 0.75rem;
                border: none;
                background-color: ${sendInviteEmail
                  ? theme.ocean
                  : theme.mono30};
                cursor: pointer;
                transition: background-color 0.2s;
                flex-shrink: 0;

                &::after {
                  content: '';
                  position: absolute;
                  top: 0.125rem;
                  left: ${sendInviteEmail ? '1.625rem' : '0.125rem'};
                  width: 1.25rem;
                  height: 1.25rem;
                  border-radius: 50%;
                  background-color: ${theme.mono0};
                  transition: left 0.2s;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                }

                &:focus {
                  outline: none;
                  box-shadow: 0 0 0 3px ${theme.deepOcean}20;
                }
              `}
            />
          </div>
        </div>

        {/* General Error Message */}
        {errors.general && (
          <div css={styles.errorMessage}>{errors.general}</div>
        )}

        {/* Buttons */}
        <div
          css={css`
            display: flex;
            gap: 0.75rem;
            margin-top: 1.5rem;
            flex-shrink: 0;
          `}
        >
          <button
            type="button"
            onClick={handleSkip}
            css={styles.secondaryButton(theme)}
          >
            Skip for Now
          </button>
          <button
            type="button"
            onClick={handleInvite}
            disabled={isSubmitting}
            css={styles.primaryButton(theme)}
          >
            {isSubmitting ? 'Sending...' : 'Send Invites'}
          </button>
        </div>
      </div>

      {/* Fixed Footer - Help Link */}
      <div css={styles.fixedFooter}>
        <div css={styles.helpLink(theme)}>
          Need help? <a href="mailto:support@birdi.io">Contact support</a>
        </div>
      </div>
    </>
  );
};

export default InviteYourTeam;
